import { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppShell from '@/components/feature/AppShell';
import { notifications } from '@/mocks/dashboard';
import { students as allStudents, classTeacherMap, type StudentRecord } from '@/mocks/students';
import { teachers as allTeachers, type TeacherRecord } from '@/mocks/teachers';
import { incidentLogs } from '@/mocks/preInspection';
import StudentFilters from '@/pages/dashboard/admin/students/components/StudentFilters';
import StudentTable from '@/pages/dashboard/admin/students/components/StudentTable';
import EnrollmentModal from '@/pages/dashboard/admin/students/components/EnrollmentModal';
import StudentDetailModal from '@/pages/dashboard/admin/students/components/StudentDetailModal';
import TeacherDetailModal from '@/pages/dashboard/admin/teachers/components/TeacherDetailModal';

export default function StudentListPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const initialClass = searchParams.get('class') || '';
  const initialTeacher = searchParams.get('teacher') || '';
  const [search, setSearch] = useState(initialTeacher);
  const [selectedClass, setSelectedClass] = useState(initialClass);
  const [showInactive, setShowInactive] = useState(false);
  const [showIncidentsOnly, setShowIncidentsOnly] = useState(false);
  const [studentData, setStudentData] = useState<StudentRecord[]>(allStudents);
  const [enrollmentOpen, setEnrollmentOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<StudentRecord | null>(null);
  const [selectedTeacher, setSelectedTeacher] = useState<TeacherRecord | null>(null);

  // Precompute incident student IDs for filtering
  const incidentStudentIds = useMemo(() => {
    const ids = new Set<string>();
    incidentLogs.forEach((inc) => ids.add(inc.studentId));
    return ids;
  }, []);

  const totalIncidentStudents = incidentStudentIds.size;

  const filteredStudents = useMemo(() => {
    let result = studentData;
    if (!showInactive) {
      result = result.filter((s) => s.status !== 'inactive');
    }
    if (showIncidentsOnly) {
      result = result.filter((s) => incidentStudentIds.has(s.studentId));
    }
    if (selectedClass) {
      result = result.filter((s) => s.className === selectedClass);
    }
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      result = result.filter(
        (s) =>
          s.name.toLowerCase().includes(q) ||
          s.studentId.toLowerCase().includes(q) ||
          s.fatherName.toLowerCase().includes(q) ||
          s.motherName.toLowerCase().includes(q) ||
          s.classTeacher.toLowerCase().includes(q) ||
          s.aadhaarNumber.includes(q) ||
          s.udidNumber.toLowerCase().includes(q)
      );
    }
    return result;
  }, [studentData, selectedClass, search, showInactive, showIncidentsOnly, incidentStudentIds]);

  const handleSaveDraft = (data: Partial<StudentRecord>) => {
    const newStudent: StudentRecord = {
      id: `stu-${Date.now()}`,
      studentId: `STU-${new Date().getFullYear()}-${String(studentData.length + 1).padStart(3, '0')}`,
      name: data.name || '',
      dob: data.dob || '',
      address: data.address || '',
      aadhaarNumber: data.aadhaarNumber || '',
      udidNumber: data.udidNumber || '',
      className: data.className || '',
      classTeacher: classTeacherMap[data.className || ''] || '',
      fatherName: data.fatherName || '',
      motherName: data.motherName || '',
      contactNumberF: data.contactNumberF || '',
      contactNumberM: data.contactNumberM || '',
      email: data.email || '',
      district: data.district || '',
      town: data.town || '',
      status: 'draft',
      documents: [],
    };
    setStudentData((prev) => [newStudent, ...prev]);
  };

  const handleSubmit = (data: Partial<StudentRecord>) => {
    const newStudent: StudentRecord = {
      id: `stu-${Date.now()}`,
      studentId: `STU-${new Date().getFullYear()}-${String(studentData.length + 1).padStart(3, '0')}`,
      name: data.name || '',
      dob: data.dob || '',
      address: data.address || '',
      aadhaarNumber: data.aadhaarNumber || '',
      udidNumber: data.udidNumber || '',
      className: data.className || '',
      classTeacher: classTeacherMap[data.className || ''] || '',
      fatherName: data.fatherName || '',
      motherName: data.motherName || '',
      contactNumberF: data.contactNumberF || '',
      contactNumberM: data.contactNumberM || '',
      email: data.email || '',
      district: data.district || '',
      town: data.town || '',
      status: 'enrolled',
      enrolledDate: new Date().toISOString().split('T')[0],
      documents: [],
    };
    setStudentData((prev) => [newStudent, ...prev]);
  };

  const handleSaveStudent = (updated: StudentRecord) => {
    setStudentData((prev) =>
      prev.map((s) => (s.id === updated.id ? updated : s))
    );
  };

  const handleStudentStatusChange = (student: StudentRecord, newStatus: string) => {
    setStudentData((prev) =>
      prev.map((s) => (s.id === student.id ? { ...s, status: newStatus as StudentRecord['status'], deactivatedAt: undefined } : s))
    );
  };

  const handleSaveTeacher = (updated: TeacherRecord) => {
    setSelectedTeacher(updated);
  };

  const classTeacherRecord = useMemo(() => {
    if (!selectedClass) return null;
    const teacherName = classTeacherMap[selectedClass];
    if (!teacherName) return null;
    return allTeachers.find((t) => t.name === teacherName && t.className === selectedClass) || null;
  }, [selectedClass]);

  return (
    <AppShell
      pageTitle={t('students.pageTitle')}
      pageSubtitle={t('students.pageSubtitle')}
      breadcrumbs={[
        { label: t('common.home') },
        { label: t('common.dashboard'), onClick: () => navigate('/dashboard/admin') },
        { label: t('students.pageTitle') },
      ]}
      notifications={notifications}
    >
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <p className="text-sm text-foreground-500 font-label">
            <span dangerouslySetInnerHTML={{ __html: t('students.totalEnrolled', { count: studentData.length, interpolation: { escapeValue: false } }) }} />
          </p>
        </div>
      </div>

      {/* Class Context Banner — shown when arriving from a class link */}
      {selectedClass && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5 p-4 bg-primary-50 rounded-xl border border-primary-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100">
              <i className="ri-group-line text-lg text-primary-600" />
            </div>
            <div>
              <p className="text-sm font-heading font-700 text-foreground-900">
                {selectedClass}
              </p>
              <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                <p className="text-xs text-foreground-500 font-label">
                  Class Teacher:{' '}
                  {classTeacherRecord ? (
                    <button
                      type="button"
                      onClick={() => setSelectedTeacher(classTeacherRecord)}
                      className="font-semibold text-primary-600 hover:text-primary-700 hover:underline transition-colors cursor-pointer"
                    >
                      {classTeacherMap[selectedClass]}
                    </button>
                  ) : (
                    <span className="font-semibold text-foreground-700">
                      {classTeacherMap[selectedClass] || t('students.unassigned')}
                    </span>
                  )}
                </p>
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-label font-semibold bg-primary-100 text-primary-700">
                  <i className="ri-user-line text-xs" />
                  {filteredStudents.length} {filteredStudents.length !== 1 ? t('students.enrolledCountPlural', { count: filteredStudents.length }) : t('students.enrolledCount', { count: filteredStudents.length })}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={() => navigate('/dashboard/admin/teachers')}
            className="inline-flex items-center gap-1.5 text-sm font-label font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line" />
            {t('students.backToFaculty')}
          </button>
        </div>
      )}

      {/* Filters + Add */}
      <StudentFilters
        search={search}
        onSearchChange={setSearch}
        selectedClass={selectedClass}
        onClassChange={setSelectedClass}
        showInactive={showInactive}
        onToggleInactive={setShowInactive}
        showIncidentsOnly={showIncidentsOnly}
        onToggleIncidents={setShowIncidentsOnly}
        incidentCount={totalIncidentStudents}
        onAddStudent={() => setEnrollmentOpen(true)}
      />

      {/* Student Table */}
      <StudentTable students={filteredStudents} onRowClick={setSelectedStudent} onStatusChange={handleStudentStatusChange} />

      {/* Enrollment Modal */}
      <EnrollmentModal
        open={enrollmentOpen}
        onClose={() => setEnrollmentOpen(false)}
        onSaveDraft={handleSaveDraft}
        onSubmit={handleSubmit}
      />

      {/* Student Detail / Edit Modal */}
      <StudentDetailModal
        open={!!selectedStudent}
        student={selectedStudent}
        onClose={() => setSelectedStudent(null)}
        onSave={handleSaveStudent}
      />

      {/* Teacher Detail Modal (opened from class banner) */}
      <TeacherDetailModal
        open={!!selectedTeacher}
        teacher={selectedTeacher}
        onClose={() => setSelectedTeacher(null)}
        onSave={handleSaveTeacher}
      />
    </AppShell>
  );
}